const valueCache = new Map();
const inFlightCache = new Map();

export function getCachedValue(key) {
  const record = valueCache.get(key);
  if (!record) return undefined;

  if (record.expiresAt <= Date.now()) {
    valueCache.delete(key);
    return undefined;
  }

  return record.value;
}

export function setCachedValue(key, value, ttlMs = 60_000) {
  valueCache.set(key, {
    value,
    expiresAt: Date.now() + Math.max(0, Number(ttlMs) || 0),
  });
  return value;
}

const IN_FLIGHT_TIMEOUT_MS = 30_000; // 30 second timeout for in-flight requests
const inFlightTimestamps = new Map();

export async function cachedRequest(key, loader, options = {}) {
  const { ttlMs = 60_000, forceRefresh = false } = options;

  if (!forceRefresh) {
    const cached = getCachedValue(key);
    if (cached !== undefined) {
      return cached;
    }
  }

  // Check for stale in-flight requests (older than timeout)
  if (inFlightCache.has(key)) {
    const startTime = inFlightTimestamps.get(key) || 0;
    const isStale = Date.now() - startTime > IN_FLIGHT_TIMEOUT_MS;
    if (isStale) {
      inFlightCache.delete(key);
      inFlightTimestamps.delete(key);
    } else {
      return inFlightCache.get(key);
    }
  }

  const promise = Promise.resolve()
    .then(loader)
    .then((result) => {
      setCachedValue(key, result, ttlMs);
      inFlightCache.delete(key);
      inFlightTimestamps.delete(key);
      return result;
    })
    .catch((error) => {
      inFlightCache.delete(key);
      inFlightTimestamps.delete(key);
      throw error;
    });

  inFlightCache.set(key, promise);
  inFlightTimestamps.set(key, Date.now());
  return promise;
}

export function invalidateCachedValue(key) {
  valueCache.delete(key);
  inFlightCache.delete(key);
  inFlightTimestamps.delete(key);
}

export function invalidateCacheByPrefix(prefix) {
  for (const key of valueCache.keys()) {
    if (key.startsWith(prefix)) {
      valueCache.delete(key);
    }
  }
  for (const key of inFlightCache.keys()) {
    if (key.startsWith(prefix)) {
      inFlightCache.delete(key);
      inFlightTimestamps.delete(key);
    }
  }
}

export function clearRequestCache() {
  valueCache.clear();
  inFlightCache.clear();
  inFlightTimestamps.clear();
}
